import {
  AddressDto,
  CreateVisitForm,
  ScheduledEventDto,
  VisitCreationRequestDto,
  VisitDto,
  VisitUpdateRequestDto,
} from '@act/shared/data-transfer-objects';
import { SelectOption } from '@act/storybook';
import { visitDateTimeHelpers } from 'utilities/dateTimeGenerator.util';
import { AddressFormatter } from 'utilities/formatters/AddressFormatter';
import { DialogFooterAction } from '../dialog/DialogFooter';

/** Outcome metadata used when building visit save payloads. */
export type VisitOutcomeLike = {
  id: number;
  text?: string;
  key?: string;
};

/**
 * Resolves the default start-time option closest to the current time of day.
 * @param startTimeOptions Available start times for the visit date.
 * @returns Default start time value in milliseconds from midnight.
 */
export function resolveDefaultVisitStartTime(
  startTimeOptions: SelectOption<number>[]
): number {
  const now = visitDateTimeHelpers.getTimeValue(new Date().getTime());

  if (startTimeOptions.length === 0) {
    return now;
  }

  for (let i = 0; i < startTimeOptions.length; i++) {
    if (startTimeOptions[i].value > now) {
      if (i === 0) {
        return startTimeOptions[0].value;
      }
      return startTimeOptions[i - 1].value;
    }
  }

  return startTimeOptions[startTimeOptions.length - 1].value;
}

/**
 * Builds location select options for a member visit.
 * @param addresses Member addresses.
 * @returns Address options including an "Other" sentinel.
 */
export function buildVisitAddressOptions(
  addresses: AddressDto[] | undefined
): SelectOption<number>[] {
  const activeAddresses = addresses?.filter(address => !address.deletedAt) || [];
  const curatedAddresses = activeAddresses.map(address => ({
    label: `${AddressFormatter.toLong(address)}`,
    value: address.id,
  }));

  return [...curatedAddresses, { label: 'Other', value: -1 }];
}

/**
 * Builds the API payload for creating or updating a visit.
 * @param params Form values, member, and optional existing visit.
 * @returns Visit create/update request body.
 */
export function buildVisitSaveRequestBody({
  data,
  userId,
  visit,
  mode,
  scheduledEvent,
  selectedOutcomeText,
}: {
  data: CreateVisitForm;
  userId: string;
  visit?: VisitDto;
  mode: 'Create' | 'Edit';
  scheduledEvent?: ScheduledEventDto;
  selectedOutcomeText?: string;
}): VisitCreationRequestDto | VisitUpdateRequestDto {
  const dateMillis = visitDateTimeHelpers.epochMillisFromDateString(data.date);
  const startTimeMillis = dateMillis + data.start;
  const endTimeMillis = startTimeMillis + data.duration;

  return {
    ...(!!visit && { visitId: visit.id }),
    subjectUserId: userId,
    description: null,
    addressId: data.addressId !== -1 ? data.addressId : null,
    location: data.location ?? null,
    startTime: startTimeMillis,
    endTime: endTimeMillis,
    outcomeId: data.outcomeId,
    outcomeDetails: selectedOutcomeText ?? '',
    note: data.note,
    scheduledEventId: mode === 'Create' ? scheduledEvent?.id : null,
    ...(data.isHospitalizationFollowUp && {
      isHospitalizationFollowUp: true,
    }),
  };
}

/**
 * @param mode Visit dialog mode.
 * @param firstName Member first name.
 * @param lastName Member last name.
 * @returns Dialog title text.
 */
export function getVisitDialogTitle(
  mode: 'Create' | 'Edit',
  firstName: string | undefined,
  lastName: string | undefined
): string {
  return `${mode === 'Create' ? 'New' : 'Edit'} Visit for ${firstName} ${lastName}`;
}

/**
 * Builds footer actions for the visit dialog.
 * @param params Save state, delete eligibility, and callbacks.
 * @returns Footer action definitions.
 */
export function buildVisitDialogActions({
  canDelete,
  isSaving,
  handleFormClose,
  onDeleteClick,
}: {
  canDelete: boolean;
  isSaving: boolean;
  handleFormClose: () => void;
  onDeleteClick: () => void;
}): DialogFooterAction[] {
  const actions: DialogFooterAction[] = [
    {
      text: 'Cancel',
      callback: () => handleFormClose(),
      variant: 'outlined',
    },
    {
      text: isSaving ? 'Saving...' : 'Save',
      type: 'submit',
      variant: 'contained',
      disabled: isSaving,
    },
  ];

  if (canDelete) {
    actions.unshift({
      text: 'Delete',
      isLeft: true,
      callback: () => onDeleteClick(),
    });
  }

  return actions;
}
